import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";

import { CommonModule } from "@angular/common";
import { MatTableModule } from "@angular/material/table";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatCardModule } from "@angular/material/card";
import { NotebookDisplayed } from "../interfaces/notebook-displayed";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ViewChild } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { Router } from "@angular/router";
import feather from "feather-icons";
import {
  interval,
  startWith,
  Subject,
  switchMap,
  timer,
  takeWhile,
  filter,
  takeUntil,
  combineLatest,
  map,
  Observable,
  take,
  firstValueFrom,
} from "rxjs";
import { NotebookService } from "../services/notebook.service";
import { handleNotebookResponse } from "../utility-functions/response-handler";
import { NotebookActionsMenuComponent } from "../components/notebook-actions-menu/notebook-actions-menu.component";
import { SnackbarService } from "../services/snackbar.service";
import { ConsumptionService } from "../services/consumption.service";
import { UserUsage } from "../interfaces/user-usage";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "../components/confirm-dialog/confirm-dialog.component";

import {
  STOP_QUOTA_MESSAGE,
  START_QUOTA_MESSAGE,
  DELETE_MESSAGE,
  NOTEBOOK_TARGET_STATUSES,
  NOTEBOOK_TRANSITIONAL_STATUSES,
  NOTEBOOK_STATUS_LABELS,
  DEFAULT_MAX_USER_USAGE,
  GATEWAY_ERROR_MESSAGE,
  DELETE_FAILED_NOTEBOOKS,
  SOMETHING_WENT_WRONG_MESSAGE,
  UPDATE_STATUS_DELAY,
  SERVICE_SHORT_DESCRIPTION
} from "../config/notebook-config";
//import { FailedNotebookBannerService } from "../services/failed-notebook-banner.service";

@Component({
  standalone: true,
    selector: "app-notebook-list",
    imports: [
        CommonModule,
        FormsModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        MatCardModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTableModule,
        NotebookActionsMenuComponent,
        MatDialogModule,
    ],
    templateUrl: "./notebook-list.component.html",
    styleUrls: ["./notebook-list.component.css"]
})
export class NotebookListComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly stopQuotaMessage = STOP_QUOTA_MESSAGE;
  readonly startQuotaMessage = START_QUOTA_MESSAGE;
  readonly deleteMessage = DELETE_MESSAGE;
  //readonly somethingWentWrongMessage = SOMETHING_WENT_WRONG_MESSAGE;

  readonly notebookTargetStatuses = NOTEBOOK_TARGET_STATUSES;
  readonly transitionalStatuses = NOTEBOOK_TRANSITIONAL_STATUSES;
  readonly notebookStatusLabels = NOTEBOOK_STATUS_LABELS;
  readonly gatewayQuotaMessage = GATEWAY_ERROR_MESSAGE;
  readonly deleteFailedNb = DELETE_FAILED_NOTEBOOKS;

  private recentStatusChanges = new Map<string, number>();
  private readonly updateStatusDelay = UPDATE_STATUS_DELAY; //30000 worked

  //descriptionText: string ="Quantum Service allows you to discover and use the Quantum Emulators made available by OVHCloud.";

  total = 0;
  notebooks$!: Observable<NotebookDisplayed[]>;
  searchText: string = "";
  dataSource = new MatTableDataSource<NotebookDisplayed>();
  showGpuColumn = false; //true for tenant
  readonly displayedColumns = [
    "name",
    "environment",
    "version",
    "cpu",
    ...(this.showGpuColumn ? ["gpu"] : []),
    "runtime",
    "status",
    "access",
    "actions",
  ];

  pageSize = 5;
  pageSizeOptions = [5, 10, 20, 50];
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  sortedColumn: string = "";
  sortDirection: "asc" | "desc" = "asc";
  pollingIntervalFast = 10000; //update interval for nb with transitional status
  pollingIntervalSlow = 30000; //update interval for nb with target/stable status

  readonly maxAllowedUsage = DEFAULT_MAX_USER_USAGE;
  private destroy$ = new Subject<void>();
  usage$!: Observable<UserUsage>;
  quotaUsed$!: Observable<{
    cpu: { label: string; width: string };
    ram: { label: string; width: string };
    storage: { label: string; width: string }
  }>;
  transitionalNotebookIds: Set<string> = new Set();
  failedNotebooks: NotebookDisplayed[] = [];
  private deletedNotebookIds = new Set<string>();
  descriptionText1: string = SERVICE_SHORT_DESCRIPTION;
   

  constructor(
    private notebookService: NotebookService,
    private router: Router,
    private snackbarService: SnackbarService,
    private consumptionService: ConsumptionService,
    private dialog: MatDialog,
    //private bannerService: FailedNotebookBannerService
  ) {
    this.dataSource.filterPredicate = (
      data: NotebookDisplayed,
      filter: string
    ) => {
      return Object.values(data).some((val) =>
        val?.toString().toLowerCase().includes(filter)
      );
    };
  }
/********************* lifecycle **************************** */
  ngOnInit() {
    //saved paginator setup from session storage
    this.loadSavedPageSize();
    this.getNotebookList();
    this.pollTargetStatusNotebooks(this.pollingIntervalSlow);
    // init usage
    this.consumptionService.initUsage(this.notebookService);
    this.consumptionService.initQuotaMonitoring(this.notebookService);
    this.usage$ = this.consumptionService.getCurrentUsage$();
    this.quotaUsed$ = this.consumptionService.getQuotaUsed$();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    setTimeout(() => {
      feather.replace();
      this.resizeChevronFeatherIcons();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.consumptionService.cleanupQuotaMonitoring();
  }

  /********************* notebooks handling **************************** */
  //monitore nb with transitional status
  private waitForNotebookState(
    notebookId: string,
    targetStates: string[],
    intervalMs: number = this.pollingIntervalFast
  ): Observable<NotebookDisplayed> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.notebookService.getNotebookById(notebookId)),
      takeWhile((notebook) => {
        const status = notebook.status?.toLowerCase();
        const isFinal = targetStates.includes(status);
        const isFailed = status === this.notebookStatusLabels["failed"];
        return !isFinal;
      }, true)
    );
  }

  private updateNotebookWithConditionalCooldown(
    updatedNotebook: NotebookDisplayed
  ): void {
    const index = this.dataSource.data.findIndex(
      (nb) => nb.id === updatedNotebook.id
    );
    if (index === -1) return;

    const oldStatus = this.dataSource.data[index].status?.toLowerCase();
    const newStatus = updatedNotebook.status?.toLowerCase();

    const wasTransitional = this.transitionalStatuses.includes(oldStatus || "");
    const isNowTarget = this.notebookTargetStatuses.includes(newStatus || "");

    const now = Date.now();

    if (wasTransitional && isNowTarget) {
      const lastChange = this.recentStatusChanges.get(updatedNotebook.id) || 0;
      const withinCooldown = now - lastChange < this.updateStatusDelay;
      if (withinCooldown) return;

      this.recentStatusChanges.set(updatedNotebook.id, now);

      setTimeout(() => {
        this.updateNotebookInDataSource(updatedNotebook);
        //this.checkAndHandleFailedNotebooks(); 
      }, this.updateStatusDelay);
    } else {
      this.updateNotebookInDataSource(updatedNotebook);
      //this.checkAndHandleFailedNotebooks(); 
    }
  }

  //update only notebook that reached target state
  private updateNotebookInDataSource(updatedNotebook: NotebookDisplayed): void {
    const index = this.dataSource.data.findIndex(
      (nb) => nb.id === updatedNotebook.id
    );
    if (index !== -1) {
      this.dataSource.data[index] = updatedNotebook;
      this.dataSource._updateChangeSubscription(); // to notify ang material table of changes
    }
  }

  updateNotebookStatusLocally(id: string) {
    const index = this.dataSource.data.findIndex((nb) => nb.id === id);
    if (index !== -1) {
      this.dataSource.data[index].status = this.notebookStatusLabels["pending"];
      this.dataSource._updateChangeSubscription(); // notify angular material table
    }
  }

/*   private checkAndHandleFailedNotebooks(): void {
    const failed = this.dataSource.data.filter(
      (nb) => nb.status?.toLowerCase() === this.notebookStatusLabels["failed"]//
    );
    this.failedNotebooks = failed;
    if (failed.length > 0) {
      this.bannerService.show(this.somethingWentWrongMessage);
    } else {
      this.bannerService.dismiss();

    }
  } */

  private refreshNotebookList(): void {
    this.notebookService.getAllNotebooks().subscribe((notebooks) => {
      this.dataSource.data = notebooks;
      this.total = notebooks.length;

      //this.checkAndHandleFailedNotebooks();
    });
  }

  private pollTargetStatusNotebooks(intervalMs: number): void {
    interval(intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.notebookService.getAllNotebooks()),
        map((notebooks) =>
          notebooks.filter((nb) =>
            this.notebookTargetStatuses.includes(nb.status?.toLowerCase())
          )
        )
      )
      .subscribe((targetNotebooks) => {
        for (const updatedNotebook of targetNotebooks) {
          this.updateNotebookWithConditionalCooldown(updatedNotebook);
        }
      });
  }

  private getNotebookList(): void {
    this.failedNotebooks = [];

    this.notebookService
      .getAllNotebooks()
      .pipe(takeUntil(this.destroy$))
      .subscribe((notebooks) => {
        if (
          JSON.stringify(this.dataSource.data) !== JSON.stringify(notebooks)
        ) {
          this.dataSource.data = notebooks;
          this.total = notebooks.length;
        }

        this.transitionalNotebookIds.clear();

        notebooks.forEach((nb) => {
          const status = nb.status?.toLowerCase();

          if (this.transitionalStatuses.includes(status)) {
            this.transitionalNotebookIds.add(nb.id);
            this.waitForNotebookState(
              nb.id,
              this.notebookTargetStatuses
            ).subscribe({
              next: (updatedNotebook) => {
                this.transitionalNotebookIds.delete(nb.id);
                this.updateNotebookWithConditionalCooldown(updatedNotebook);
              },
              error: (err) => console.error("Polling failed", err),
            });
          }
        });
        ////this.checkAndHandleFailedNotebooks();
      });
  }

  isRunning(nb: NotebookDisplayed): boolean {
    return (
      nb.status?.toLowerCase() ===
      this.notebookStatusLabels["running"].toLowerCase()
    );
  }

  isStopped(nb: NotebookDisplayed): boolean {
    return (
      nb.status?.toLowerCase() ===
      this.notebookStatusLabels["stopped"].toLowerCase()
    );
  }

 //****************************** notebooks actions ********************************
  deleteNotebook(notebookId: string) {
    this.notebookService.getNotebookNameById(notebookId).subscribe((name) => {
      const message = `${this.deleteMessage} "${name}"?`;

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message },
        backdropClass: "blurred-backdrop",
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.notebookService.deleteNotebook(notebookId).subscribe({
            next: () => {
              handleNotebookResponse(204, "Notebook delete");
              this.refreshNotebookList();
              this.consumptionService.loadAndCalculateUsage(this.notebookService);
            },
            error: (err) => {
              const customMessage = err.error?.message || err.message;
              handleNotebookResponse(
                err.status,
                "Notebook delete",
                customMessage
              );
            },
          });
        }
      });
    });
  }

  onStopNotebook(event: { id: string }) {
    const { id } = event;
    this.consumptionService.loadAndCalculateUsage(this.notebookService);
    //quota check
/*     
    this.consumptionService
      .isStoppedLimitExceeded()
      .pipe(take(1))
      .subscribe((exceeded) => {
        if (exceeded) {
          this.openSnackBarWithAction(this.stopQuotaMessage, "✕", "error");
          console.log("Stopped nb limit Exceeded, cant stop nb");
        } else {
          
          //this.refreshNotebookList();
        }
      }); */
      this.stopNotebook(id);
  }

  private stopNotebook(notebookId: string): void {
    this.refreshNotebookList();
    if (this.transitionalNotebookIds.has(notebookId)) {
      console.warn(
        `Notebook ${notebookId} is already in a transitional state.`
      );
      return;
    }

    const notebook = this.dataSource.data.find((nb) => nb.id === notebookId);
    const currentStatus = notebook?.status?.toLowerCase();

    if (
      !notebook ||
      (!this.transitionalStatuses.includes(currentStatus) &&
        currentStatus !== this.notebookStatusLabels["running"].toLowerCase() &&
        currentStatus !== this.notebookStatusLabels["failed"].toLowerCase())
    ) {
      console.warn(`Notebook ${notebookId} is not in a stoppable state`);
      return;
    }

    this.transitionalNotebookIds.add(notebookId);
    this.refreshNotebookList();

    this.notebookService.stopNotebook(notebookId).subscribe({
      next: () => {
        handleNotebookResponse(204, "Notebook stop");

        this.waitForNotebookState(notebookId, [
          this.notebookStatusLabels["stopped"],
        ]).subscribe({
          next: (updatedNotebook) => {
            this.transitionalNotebookIds.delete(notebookId);

            // Delay update to UI for 30 seconds

            this.updateNotebookWithConditionalCooldown(updatedNotebook);
            ////this.checkAndHandleFailedNotebooks();
          },
          error: (err) => {
            this.transitionalNotebookIds.delete(notebookId);
            
            console.error("Polling failed", err);
          },
        });
      },
    error: (err) => {
      this.transitionalNotebookIds.delete(notebookId);

      if (err?.status === 402 && err?.error?.message === "gateway: quota exceeded") {
        this.openSnackBarWithAction(this.stopQuotaMessage, "✕", "error");
        console.log ("GW: storage acceeded. Cant stop notebook");
      } else if (
        err?.status === 400 &&
        err?.error?.message?.includes("already stopping")
      ) {
        console.warn(`Notebook ${notebookId} is already stopping.`);
      } else {
        const customMessage = err?.error?.message || err?.message;
        handleNotebookResponse(err?.status, "Notebook stop", customMessage);
      }
    },
    });
  }

  onStartNotebook(event: { id: string; cpu: number }) {
    const { id, cpu } = event;
    const notebook = this.dataSource.data.find((nb) => nb.id === id);

    if (!notebook) return;

    const currentStatus = notebook.status.toLowerCase();
    const isAlreadyStarting = this.transitionalStatuses.includes(currentStatus);

    if (isAlreadyStarting) {
      return;
    }
   this.consumptionService.loadAndCalculateUsage(this.notebookService);
    // CPU quota check before starting
    
/*     this.consumptionService
      .isCpuExceeded(cpu)
      .pipe(take(1))
      .subscribe((exceeded) => {
        if (exceeded) {
          this.openSnackBarWithAction(this.startQuotaMessage, "✕", "error");
        } else {
          // this.updateNotebookStatusLocally(id);
          
          //this.refreshNotebookList();
        }
      }); */
      this.startNotebook(id);
  }

  private startNotebook(id: string): void {
   
    this.refreshNotebookList();
    if (this.transitionalNotebookIds.has(id)) {
      console.warn(
        `Notebook ${id} is already in a transitional state.`
      );
      return;
    }

    const notebook = this.dataSource.data.find((nb) => nb.id === id);
    const currentStatus = notebook?.status?.toLowerCase();

    if (
      !notebook ||
      (!this.transitionalStatuses.includes(currentStatus) &&
        currentStatus !== this.notebookStatusLabels["stopped"].toLowerCase()&&currentStatus !== this.notebookStatusLabels["failed"].toLowerCase())
    ) {
      console.warn(`Notebook ${id} is not in a startable state`);
      return;
    }
    this.refreshNotebookList();


    this.notebookService.startNotebook(id).subscribe({
      next: () => {
        handleNotebookResponse(200, "Notebook started");
        this.waitForNotebookState(id, this.notebookTargetStatuses).subscribe({
          next: (updatedNotebook) => {
            this.transitionalNotebookIds.delete(id);

            this.updateNotebookWithConditionalCooldown(updatedNotebook);
            //this.checkAndHandleFailedNotebooks();
          },

          error: (err) => console.error("Polling failed", err),
        });
      },
      error: (err) => {
        if (
          err.status === 402 &&
          err.error?.message === "gateway: quota exceeded"
        ) {
          this.openSnackBarWithAction(this.startQuotaMessage, "✕", "error");
          console.log("GW: CPU limit exceeded. Can't start notebook.");
        } else {
          const customMessage = err.error?.message || err.message;
          handleNotebookResponse(err.status, "Notebook start", customMessage);
        }
      },
    });
  }

 /********************* ui utilities & Table Handling **************************** */
  private parseRuntimeToMinutes(runtime: string): number {
    const hoursMatch = runtime.match(/(\d+)h/);
    const minutesMatch = runtime.match(/(\d+)m/);

    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

    return hours * 60 + minutes;
  }

  sortData(column: keyof NotebookDisplayed) {
    if (this.sortedColumn === column) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortedColumn = column;
      this.sortDirection = "asc";
    }

    const sorted = this.dataSource.data.slice().sort((a, b) => {
      const aVal = this.getComparableValue(a, column);
      const bVal = this.getComparableValue(b, column);
      return this.sortDirection === "asc"
        ? aVal.localeCompare?.(bVal) ?? aVal - bVal
        : bVal.localeCompare?.(aVal) ?? bVal - aVal;
    });

    this.dataSource.data = sorted;
    setTimeout(() => this.refreshFeatherIcons(), 0);
    // console.log(`Sorted ${column} ${this.sortDirection}`);
  }

  capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  refreshFeatherIcons() {
    const headers = document.querySelectorAll("mat-header-cell");

    const columnLabelMap: { [label: string]: string } = {
      name: "name",
      environment: "environment",
      version: "version",
      cpu: "cpu",
      "operating time": "runtime",
      status: "status",
    };

    headers.forEach((header) => {
      const icon = header.querySelector("i[data-feather]");
      const svg = header.querySelector("svg.feather");

      if (svg) svg.remove();
      if (icon) icon.remove();

      const headerText = header.textContent?.trim().toLowerCase();
      const dataColumn = columnLabelMap[headerText || ""];

      if (dataColumn) {
        const newIcon = document.createElement("i");
        const isSorted = this.sortedColumn === dataColumn;
        const featherIcon = isSorted
          ? this.sortDirection === "asc"
            ? "chevron-down"
            : "chevron-up"
          : "chevron-down";

        newIcon.setAttribute("data-feather", featherIcon);
        newIcon.classList.add("sort-icon");
        header.appendChild(newIcon);
      }
    });

    feather.replace();
    this.resizeChevronFeatherIcons();
  }
  resizeChevronFeatherIcons(): void {
    const icons = document.querySelectorAll(
      "svg.feather.feather-chevron-down, svg.feather.feather-chevron-up"
    );
    icons.forEach((icon) => {
      icon.setAttribute("width", "16");
      icon.setAttribute("height", "16");
      (icon as SVGElement).style.width = "16px";
      (icon as SVGElement).style.height = "16px";
      (icon as SVGElement).style.flexShrink = "0"; // prevent squeezing
    });
  }

  private getComparableValue(
    item: NotebookDisplayed,
    column: keyof NotebookDisplayed
  ): any {
    if (column === "runtime") {
      return this.parseRuntimeToMinutes(item.runtime).toString();
    }
    const val = item[column];
    return typeof val === "string" ? val.toLowerCase() : val;
  }
  applyFilter() {
    this.dataSource.filter = this.searchText.trim().toLowerCase();
  }

  clearSearch() {
    this.searchText = "";
    this.applyFilter();
  }

  //save in session storage paginator settings
  onPageEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    sessionStorage.setItem("notebookPageSize", event.pageSize.toString());
  }
  
  private loadSavedPageSize(): void {
    const savedPageSize = sessionStorage.getItem("notebookPageSize");
    if (savedPageSize) {
      this.pageSize = +savedPageSize;
    }
  }


  onCreateNewNotebook() {
    // console.log('Create new clicked');
    this.router.navigateByUrl("new");
  }

  goToDetails(notebookId: string): void {
    this.router.navigate(["details", notebookId]);
  }

  openSnackBar(msg: string) {
    this.snackbarService.openSnackBar(msg);
  }

  openSnackBarWithAction(
    msg: string,
    action: string,
    type: "success" | "error"
  ) {
    this.snackbarService.openSnackBarWithAction(msg, action, type);
  }

}
